import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../../i18n';
import type { 
  SettlementItem, 
  SettlementSearchMeta, 
  SearchSettlementsResponse,
  CommissionSumResponse,
  CommissionSumItem,
  CommissionSumMeta,
  CommissionSumExtra,
  CommissionSearchRequest,
  CommissionSearchResponse,
  CommissionSearchItem,
  CommissionSearchMeta,
  TransactionSearchRequest,
  TransactionSearchResponse,
  TransactionSearchItem,
  TransactionSearchMeta,
} from '../Domain/entity/SettlementTypes';
import { searchSettlements, getCommissionSum, searchCommissions, searchTransactions } from './settlementActions';

type SearchStatus = 'idle' | 'searching' | 'searched' | 'failed';
type CommissionSumStatus = 'idle' | 'loading' | 'loaded' | 'failed';
type CommissionSearchStatus = 'idle' | 'searching' | 'searched' | 'failed';
type TransactionSearchStatus = 'idle' | 'searching' | 'searched' | 'failed';

export type SettlementState = {
  // Settlement 검색 관련
  searchStatus: SearchStatus;
  searchError?: string;
  searchResults: SettlementItem[];
  searchMeta?: SettlementSearchMeta;
  // 후원금 내역 합계 관련
  commissionSumStatus: CommissionSumStatus;
  commissionSumError?: string;
  commissionSumResults: CommissionSumItem[];
  commissionSumMeta?: CommissionSumMeta;
  commissionSumExtra?: CommissionSumExtra;
  // 후원금 내역 조회 관련
  commissionSearchStatus: CommissionSearchStatus;
  commissionSearchError?: string;
  commissionSearchResults: CommissionSearchItem[];
  commissionSearchMeta?: CommissionSearchMeta;
  // 후원자(거래) 조회 (POST /transactions/m/sch)
  transactionSearchStatus: TransactionSearchStatus;
  transactionSearchError?: string;
  transactionSearchResults: TransactionSearchItem[];
  transactionSearchMeta?: TransactionSearchMeta;
};

const initialState: SettlementState = {
  searchStatus: 'idle',
  searchResults: [],
  commissionSumStatus: 'idle',
  commissionSumResults: [],
  commissionSearchStatus: 'idle',
  commissionSearchResults: [],
  transactionSearchStatus: 'idle',
  transactionSearchResults: [],
};

const settlementSlice = createSlice({
  name: 'settlement',
  initialState,
  reducers: {
    resetSettlementState() {
      return initialState;
    },
    clearSearchResults(state) {
      state.searchStatus = 'idle';
      state.searchError = undefined;
      state.searchResults = [];
      state.searchMeta = undefined;
    },
    clearCommissionSum(state) {
      state.commissionSumStatus = 'idle';
      state.commissionSumError = undefined;
      state.commissionSumResults = [];
      state.commissionSumMeta = undefined;
      state.commissionSumExtra = undefined;
    },
    clearCommissionSearch(state) {
      state.commissionSearchStatus = 'idle';
      state.commissionSearchError = undefined;
      state.commissionSearchResults = [];
      state.commissionSearchMeta = undefined;
    },
    clearTransactionSearch(state) {
      state.transactionSearchStatus = 'idle';
      state.transactionSearchError = undefined;
      state.transactionSearchResults = [];
      state.transactionSearchMeta = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // searchSettlements
      .addCase(searchSettlements.pending, (state) => {
        state.searchStatus = 'searching';
        state.searchError = undefined;
      })
      .addCase(searchSettlements.fulfilled, (state, action: PayloadAction<SearchSettlementsResponse>) => {
        state.searchStatus = 'searched';
        state.searchResults = action.payload.data;
        state.searchMeta = action.payload.meta;
      })
      .addCase(searchSettlements.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.searchError = action.payload ?? i18n.t('common.repositoryErrors.settlementSearchFailed');
      })
      // getCommissionSum
      .addCase(getCommissionSum.pending, (state) => {
        state.commissionSumStatus = 'loading';
        state.commissionSumError = undefined;
      })
      .addCase(getCommissionSum.fulfilled, (state, action: PayloadAction<CommissionSumResponse>) => {
        state.commissionSumStatus = 'loaded';
        state.commissionSumResults = action.payload.data;
        state.commissionSumMeta = action.payload.meta;
        state.commissionSumExtra = action.payload.extra;
      })
      .addCase(getCommissionSum.rejected, (state, action) => {
        state.commissionSumStatus = 'failed';
        state.commissionSumError = action.payload ?? i18n.t('common.repositoryErrors.commissionSumFailed');
      })
      // searchCommissions
      .addCase(searchCommissions.pending, (state) => {
        state.commissionSearchStatus = 'searching';
        state.commissionSearchError = undefined;
      })
      .addCase(searchCommissions.fulfilled, (state, action) => {
        state.commissionSearchStatus = 'searched';
        const append = (action.meta.arg as CommissionSearchRequest)?.append;
        if (append) {
          // 추가 로드: 기존 데이터에 새 데이터 100건 추가
          const newData = action.payload.data ?? [];
          state.commissionSearchResults = [...(state.commissionSearchResults ?? []), ...newData];
        } else {
          state.commissionSearchResults = action.payload.data ?? [];
        }
        state.commissionSearchMeta = action.payload.meta;
      })
      .addCase(searchCommissions.rejected, (state, action) => {
        state.commissionSearchStatus = 'failed';
        state.commissionSearchError = action.payload ?? i18n.t('common.repositoryErrors.commissionSearchFailed');
      })
      // searchTransactions
      .addCase(searchTransactions.pending, (state) => {
        state.transactionSearchStatus = 'searching';
        state.transactionSearchError = undefined;
      })
      .addCase(searchTransactions.fulfilled, (state, action) => {
        state.transactionSearchStatus = 'searched';
        const append = (action.meta.arg as TransactionSearchRequest)?.append;
        if (append) {
          const newData = action.payload.data ?? [];
          state.transactionSearchResults = [...(state.transactionSearchResults ?? []), ...newData];
        } else {
          state.transactionSearchResults = action.payload.data ?? [];
        }
        state.transactionSearchMeta = action.payload.meta;
      })
      .addCase(searchTransactions.rejected, (state, action) => {
        state.transactionSearchStatus = 'failed';
        state.transactionSearchError = action.payload ?? i18n.t('common.repositoryErrors.donorSearchFailed');
      });
  },
});

export const { resetSettlementState, clearSearchResults, clearCommissionSum, clearCommissionSearch, clearTransactionSearch } = settlementSlice.actions;
export default settlementSlice.reducer;

